import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check if we have a token in localStorage
      const token = localStorage.getItem('transcend_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token with backend
      const response = await api.getAuthStatus(token);
      if (response.authenticated) {
        setUser({
          email: response.email,
          name: response.name,
          picture: response.picture
        });
      } else {
        // Token invalid, clear it
        localStorage.removeItem('transcend_token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('transcend_token');
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Redirect to backend OAuth endpoint
    const backendUrl = process.env.REACT_APP_API_URL || 'https://transcend-hyrule-827956171027.us-central1.run.app';
    window.location.href = `${backendUrl}/auth/login`;
  };

  const handleAuthCallback = (token) => {
    // Called when user returns from OAuth flow
    if (token) {
      localStorage.setItem('transcend_token', token);
      checkAuthStatus();
    }
  };

  const logout = () => {
    localStorage.removeItem('transcend_token');
    setUser(null);
    // Optionally redirect to login
    window.location.href = '/login';
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    handleAuthCallback,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
